import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { OwnerAppointment } from '@/api/appointments/appointmentsApi.types';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { APPLICATION_ROUTES } from '@/constants/applicationRoutes';
import { useVisitCalendarAndPricing } from '../UpcomingVisitsCard/useVisitCalendarAndPricing';
import { VisitRow } from '../UpcomingVisitsCard/VisitRow';

interface NextVisitCardProps {
  nextVisit: OwnerAppointment | null;
  isLoading: boolean;
}

export function NextVisitCard({ nextVisit, isLoading }: NextVisitCardProps) {
  const { t } = useTranslation('clients');
  const navigate = useNavigate();
  const { formatVisitPrice, downloadVisitCalendar } = useVisitCalendarAndPricing();

  return (
    <Card as="section">
      <div className="flex items-center gap-2 mb-3">
        <span className="w-9 h-9 rounded-xl bg-zoovpalelavender text-zooviolet flex items-center justify-center shrink-0">
          <Icon name="Calendar" />
        </span>
        <h2 className="text-sm font-bold text-zooindigo">{t('portal.upcomingVisits')}</h2>
      </div>
      {isLoading === true ? (
        <div className="py-4 flex justify-center">
          <LoadingSpinner className="w-6 h-6" />
        </div>
      ) : nextVisit === null ? (
        <p className="text-[13px] text-zoomuted py-1">{t('portal.noUpcomingVisits')}</p>
      ) : (
        <VisitRow
          visit={nextVisit}
          priceLabel={formatVisitPrice(nextVisit.therapyType)}
          onAddToCalendar={downloadVisitCalendar}
        />
      )}
      <Button
        variant="outline"
        size="chip"
        className="w-full mt-3 min-h-[44px]"
        onClick={function seeAllVisits() {
          navigate(APPLICATION_ROUTES.clientPortalVisits);
        }}
      >
        {t('portal.seeAllVisits')}
      </Button>
    </Card>
  );
}
